import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios"
import { useConsumerAuthStore } from "@/stores/consumerAuth"
import { notifyError } from "@/utils/notify"

/** CSRF portal memakai endpoint yang sama dengan admin; token disimpan satu kunci `csrf_token`. */
let csrfToken: string | null = localStorage.getItem("csrf_token")

export const fetchPortalCsrfToken = async (): Promise<string | null> => {
  try {
    const response = await portalApiClient.get<{ csrf_token: string }>("/csrf-token")
    if (response.data.csrf_token) {
      csrfToken = response.data.csrf_token
      localStorage.setItem("csrf_token", csrfToken)
    }
    return csrfToken
  } catch {
    return null
  }
}

const portalApiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "http://localhost:8080/api/v1",
  timeout: 30000,
  headers: { Accept: "application/json" },
  withCredentials: true,
})

portalApiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const consumerStore = useConsumerAuthStore()
    const token = consumerStore.token || localStorage.getItem("portal_token")
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"]
    } else {
      config.headers["Content-Type"] = "application/json"
    }
    const method = config.method?.toLowerCase() || ""
    if (["post", "put", "delete", "patch"].includes(method)) {
      if (!csrfToken) {
        await fetchPortalCsrfToken()
      }
      if (csrfToken) {
        config.headers["X-CSRF-Token"] = csrfToken
      }
    }
    return config
  },
  (error) => Promise.reject(error),
)

portalApiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    if (error.response) {
      const { status, data } = error.response
      if (status === 403 && data?.error === "csrf_token_invalid" && !originalRequest._retry) {
        originalRequest._retry = true
        await fetchPortalCsrfToken()
        return portalApiClient(originalRequest)
      }
      switch (status) {
        case 401: {
          const reqURL = String(originalRequest.url || "")
          const isAuthAttempt =
            reqURL.includes("/portal/auth/login") ||
            reqURL.includes("/portal/auth/google") ||
            reqURL.includes("/portal/auth/register")
          // 401 pada percobaan login/Google = kredensial/token ditolak, bukan sesi habis.
          if (isAuthAttempt) {
            break
          }
          if (!originalRequest._retry) {
            originalRequest._retry = true
            useConsumerAuthStore().logoutLocal()
            const cur = window.location.pathname + window.location.search
            const isConsumerLogin =
              cur === "/masuk" || cur === "/masuk/" || cur.startsWith("/masuk?")
            if (!isConsumerLogin) {
              window.location.href = `/masuk?redirect=${encodeURIComponent(cur)}`
            }
          }
          break
        }
        case 403:
          if (data?.error !== "login_required") {
            notifyError(data?.message || "Akses ditolak")
          }
          break
        default:
          if (data?.message) notifyError(data.message)
      }
    }
    return Promise.reject(error)
  },
)

export const portalGet = async <T>(url: string) => (await portalApiClient.get<T>(url)).data
export const portalPost = async <T>(url: string, data?: unknown) =>
  (await portalApiClient.post<T>(url, data)).data
export const portalPut = async <T>(url: string, data?: unknown) =>
  (await portalApiClient.put<T>(url, data)).data

export const portalDel = async <T>(url: string) => (await portalApiClient.delete<T>(url)).data

export default portalApiClient
